import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useMsal } from "@azure/msal-react";
import { useAuthFetch } from "../hooks/useAuthFetch";
import {
  createDailyReport,
  deleteDailyReport,
  getDailyReport,
  listDailyReports,
  updateDailyReport,
} from "../utils/dailyReportsApi";
import { elementToPdfBlob, pdfFileName } from "../utils/dailyReportPdf";
import { ConfirmDialog } from "./common/ConfirmDialog";
import {
  deleteProjectItem,
  deleteProjectPath,
  downloadProjectFile,
  getGraphToken,
  uploadProjectFile,
} from "../utils/projectDatabaseFiles";
import "./ElastizellDailyReport.css";

const STORAGE_KEY = "nettles_elastizell_daily_v1";
const DRAFT_KEY = "nettles_elastizell_daily_draft_v2";

const DEFAULT_SCOPE = '500,000 SF of LWIC w/6" EPS on 3" N Deck';

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function reportNamePrefix(iso) {
  const date = iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`) : new Date();
  if (Number.isNaN(date.getTime())) return "Daily report";
  const month = date.toLocaleDateString("en-US", { month: "short" });
  return `${date.getDate()}${month}${date.getFullYear()}`;
}

function defaultReportName(iso, existingNames = []) {
  const prefix = reportNamePrefix(iso);
  const pattern = new RegExp(`^${prefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}-(\\d+)$`);
  const used = new Set();
  existingNames.forEach((name) => {
    const match = pattern.exec(String(name || "").trim());
    if (match) used.add(Number(match[1]));
  });
  let sequence = 1;
  while (used.has(sequence)) sequence += 1;
  return `${prefix}-${sequence}`;
}

function formatSavedAt(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function sharePointProjectKey(projectData) {
  return String(projectData?.projectId || projectData?.projectNo || projectStorageId(projectData) || "").trim();
}


function blankSample(extra = false) {
  return { nlb: "", noz: "", time: "", extra };
}

function currentUserName(preparedBy) {
  const fromWindow = typeof window !== "undefined" ? window.__currentUserDisplayName : "";
  return String(preparedBy || fromWindow || "").trim();
}

function projectStorageId(projectData) {
  return String(projectData?.projectId || projectData?.projectNo || projectData?.opportunityNo || projectData?.id_project || "").trim();
}

function meaningfulText(value) {
  const text = String(value || "").trim();
  return text && text !== "-" ? text : "";
}

function joinAddress(parts) {
  return parts
    .map((part) => String(part || "").trim())
    .filter((part) => part && part !== "-")
    .join(", ");
}

function addressObject(value) {
  return value && typeof value === "object" ? value : {};
}

/** Pull street/city/state/zip from every shape the Salesforce APIs and local cache use. */
function addressPartsFrom(source) {
  if (!source || typeof source !== "object") return ["", "", "", ""];
  const nested = source.Project__r || {};
  const address = addressObject(source.Address || source.address);
  return [
    source.street || source.Street || source.Street_Project__c || source.Street__c
      || nested.Street__c || nested.Street_Project__c
      || address.Street || address.street || "",
    source.city || source.City || source.City_Project__c || source.gmb_City__c
      || nested.gmb_City__c || nested.City_Project__c
      || address.City || address.city || "",
    source.state || source.State || source.State_Project__c || source.gmb_Project_State__c
      || nested.gmb_Project_State__c || nested.State_Project__c
      || address.State || address.state || "",
    source.zipCode || source.ZipCode || source.Zip_Code_Project__c || source.Zip_Code__c
      || nested.Zip_Code__c || nested.Zip_Code_Project__c
      || address.ZipCode || address.zipCode || address.Zip || "",
  ];
}

function storedProjectDetails() {
  try {
    let data = JSON.parse(localStorage.getItem("selectedProjectDetails") || "{}") || {};
    if (data.records && typeof data.records === "object") data = { ...data, ...data.records };
    return data;
  } catch {
    return {};
  }
}

function listedOpportunity(projectData) {
  try {
    const list = JSON.parse(localStorage.getItem("projectsList") || "[]");
    if (!Array.isArray(list)) return null;
    const id = projectData?.id_project || projectData?.Id;
    const opportunityNo = projectData?.opportunityNo || projectData?.opportunityUUID;
    const projectId = projectData?.projectId || projectData?.projectNo;
    return list.find((row) => (
      (id && row.Id === id)
      || (opportunityNo && row.Opportunity_System_Wide_ID__c === opportunityNo)
      || (projectId && row.Project__r?.Project_System_Wide_ID__c === projectId)
    )) || null;
  } catch {
    return null;
  }
}

function projectAddressFrom(projectData) {
  const stored = storedProjectDetails();
  const listed = listedOpportunity(projectData);
  return (
    joinAddress(addressPartsFrom(projectData))
    || meaningfulText(projectData?.projectAddress)
    || joinAddress(addressPartsFrom(stored))
    || meaningfulText(stored?.projectAddress)
    || joinAddress(addressPartsFrom(listed))
    || joinAddress(addressPartsFrom(listed?.Project__r))
  );
}

/** Values still holding a placeholder that real project data should replace. */
function isPlaceholder(value, extra = "") {
  const text = String(value || "").trim();
  return !text || text === "-" || (extra && text === extra);
}

function projectScopeFrom(projectData) {
  return meaningfulText(projectData?.scopeDescription) || meaningfulText(projectData?.scope);
}

function projectSeed(projectData, preparedBy) {
  return {
    pname: projectData?.projectName || "",
    paddr: projectAddressFrom(projectData),
    pno: projectData?.projectId || projectData?.projectNo || "",
    by: currentUserName(preparedBy),
    gc: meaningfulText(projectData?.customerName),
    scope: projectScopeFrom(projectData),
  };
}

function applyProjectDetails(values, projectData, preparedBy) {
  const seed = projectSeed(projectData, preparedBy);
  ["pname", "paddr", "pno", "gc"].forEach((key) => {
    if (seed[key]) values[key] = seed[key];
    else if (isPlaceholder(values[key])) values[key] = "";
  });
  if (isPlaceholder(values.scope, DEFAULT_SCOPE)) values.scope = seed.scope || "";
  if (!values.by && seed.by) values.by = seed.by;
  return values;
}

function freshReport(projectData, preparedBy) {
  const seed = projectSeed(projectData, preparedBy);
  return {
    v: {
      date: todayIso(),
      sdate: todayIso(),
      by: "",
      eps: "",
      slurrytot: "",
      epstd: "",
      top: "",
      toptot: "",
      toptd: "",
      vol: "0.333333",
      qc: "",
      wx: "",
      coord: "",
      eq: "",
      mat: "",
      sum: "",
      plan: "",
      sig: "",
      ...seed,
    },
    samples: [blankSample(), blankSample(), blankSample(), blankSample()],
    photos: [],
  };
}

function readDraft(projectId) {
  if (!projectId) return null;
  try {
    const nextRaw = localStorage.getItem(`${DRAFT_KEY}:${projectId}`);
    if (nextRaw) {
      const draft = JSON.parse(nextRaw);
      return draft && typeof draft === "object" ? { ...draft, legacy: false } : null;
    }
    const legacyRaw = localStorage.getItem(`${STORAGE_KEY}:${projectId}`);
    if (!legacyRaw) return null;
    const saved = JSON.parse(legacyRaw);
    return { legacy: true, reportId: null, name: "", userEdited: true, v: saved.v || {}, s: saved.s || [], p: saved.p || [] };
  } catch {
    return null;
  }
}

function writeDraft(projectId, draft) {
  if (!projectId) return;
  const photos = (draft.photos || []).map((photo) => {
    const next = { ...photo };
    if (next.sharepointItemId && String(next.img || "").startsWith("data:")) delete next.img;
    if (String(next.img || "").startsWith("blob:")) delete next.img;
    return next;
  });
  localStorage.setItem(`${DRAFT_KEY}:${projectId}`, JSON.stringify({ ...draft, photos, legacy: false }));
}

function clearDraft(projectId) {
  if (!projectId) return;
  localStorage.removeItem(`${DRAFT_KEY}:${projectId}`);
  localStorage.removeItem(`${STORAGE_KEY}:${projectId}`);
}

function savedPhoto(photo, index) {
  return {
    id: photo?.id || null,
    img: photo?.img && !String(photo.img).startsWith("blob:") ? photo.img : null,
    loc: photo?.loc || photo?.location || "",
    note: photo?.note || "",
    fileName: photo?.fileName || "",
    sharepointItemId: photo?.sharepointItemId || "",
    webUrl: photo?.webUrl || "",
    imageFailed: Boolean(photo?.imageFailed),
    extra: Boolean(photo?.extra) || index >= 4,
  };
}

function loadWorkingCopy(projectData, preparedBy) {
  const base = freshReport(projectData, preparedBy);
  const empty = {
    ...base,
    photos: [],
    reportId: null,
    name: defaultReportName(base.v.date),
    userEdited: false,
  };
  const saved = readDraft(projectStorageId(projectData));
  if (!saved) return empty;
  try {
    const savedValues = { ...(saved.v || {}) };
    if (isPlaceholder(savedValues.paddr)) delete savedValues.paddr;
    if (isPlaceholder(savedValues.scope, DEFAULT_SCOPE)) delete savedValues.scope;
    const values = applyProjectDetails({ ...base.v, ...savedValues }, projectData, preparedBy);
    if (!values.date) values.date = todayIso();
    if (!values.sdate) values.sdate = values.date;
    if (!values.vol || values.vol === "0.25") values.vol = "0.333333";
    if (values.slurrytot === "500000" || values.slurrytot === "500,000") values.slurrytot = "";
    if (values.toptot === "500000" || values.toptot === "500,000") values.toptot = "";
    const date = values.date || todayIso();
    return {
      v: values,
      samples: Array.isArray(saved.s) && saved.s.length > 0
        ? saved.s.map((row, index) => ({ ...blankSample(), ...row, extra: Boolean(row?.extra) || index >= 4 }))
        : base.samples,
      photos: Array.isArray(saved.p) ? saved.p.slice(0, 12).map(savedPhoto) : [],
      reportId: saved.reportId || null,
      name: String(saved.name || "").trim() || defaultReportName(date),
      userEdited: Boolean(saved.userEdited || saved.legacy),
    };
  } catch {
    return empty;
  }
}

function reportFromServer(report, projectData, preparedBy) {
  const base = freshReport(projectData, preparedBy);
  const savedValues = { ...(report?.form || {}) };
  if (report?.signature) savedValues.sig = report.signature;
  const values = applyProjectDetails({ ...base.v, ...savedValues }, projectData, preparedBy);
  if (!values.date) values.date = report?.reportDate || todayIso();
  if (!values.sdate) values.sdate = values.date;
  return {
    v: values,
    samples: Array.isArray(report?.samples) && report.samples.length > 0
      ? report.samples.map((row, index) => ({ ...blankSample(), ...row, extra: Boolean(row?.extra) || index >= 4 }))
      : base.samples,
    photos: Array.isArray(report?.photos) ? report.photos.map(savedPhoto) : [],
    reportId: report.id,
    name: report.name || defaultReportName(values.date),
  };
}

function productionItemKey(row) {
  return `${String(row?.code || "").trim()}|${String(row?.description || row?.activity || "").trim()}`;
}

function formatQty(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "";
  return number.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function displayTotal(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return Math.round(number);
}

/** Installed today, to date, and total for one Production Tracking line as of the report date. */
function summarizeProductionLine(row, dates, reportDate) {
  const key = productionItemKey(row);
  const keyLower = key.toLowerCase();
  const ordered = (Array.isArray(dates) ? dates : [])
    .map((day) => ({
      workDate: String(day?.workDate || "").slice(0, 10),
      entries: Array.isArray(day?.entries) ? day.entries : [],
      installedToDateByItem: day?.installedToDateByItem && typeof day.installedToDateByItem === "object"
        ? day.installedToDateByItem
        : {},
    }))
    .filter((day) => /^\d{4}-\d{2}-\d{2}$/.test(day.workDate))
    .sort((a, b) => a.workDate.localeCompare(b.workDate));

  const matchEntry = (entries) => (entries || []).find((entry) => {
    const itemKey = String(entry?.itemKey || "").trim();
    return itemKey === key || itemKey.toLowerCase() === keyLower;
  });

  const onReportDay = ordered.find((day) => day.workDate === reportDate);
  const todayEntry = onReportDay ? matchEntry(onReportDay.entries) : null;
  const today = todayEntry ? Number(todayEntry.newlyInstalled) || 0 : 0;

  const through = [...ordered].reverse().find((day) => day.workDate <= reportDate);
  let toDate = 0;
  if (through) {
    const map = through.installedToDateByItem;
    const mappedKey = Object.prototype.hasOwnProperty.call(map, key)
      ? key
      : Object.keys(map).find((itemKey) => itemKey.toLowerCase() === keyLower);
    if (mappedKey) {
      toDate = Number(map[mappedKey]) || 0;
    } else {
      toDate = ordered
        .filter((day) => day.workDate <= reportDate)
        .reduce((sum, day) => {
          const entry = matchEntry(day.entries);
          return sum + (entry ? Number(entry.newlyInstalled) || 0 : 0);
        }, 0);
    }
  }

  const rawTotal = Number(row?.totalEstimated ?? row?.quantity);
  const total = Number.isFinite(rawTotal) ? rawTotal : 0;
  const percent = total > 0 ? Math.round((toDate / total) * 100) : null;
  return { today, toDate, total, percent };
}

function sampleDensity(sample, volume) {
  const lb = parseFloat(sample.nlb);
  const oz = parseFloat(sample.noz);
  const hasLb = !Number.isNaN(lb);
  const hasOz = !Number.isNaN(oz);
  if ((!hasLb && !hasOz) || !(volume > 0)) return null;
  const totalLb = (hasLb ? lb : 0) + (hasOz ? oz : 0) / 16;
  return totalLb / volume;
}

function sampleName(values, sample, index) {
  const project = (values.pname || "").trim().replace(/[^A-Za-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  const date = (values.date || "").replace(/-/g, "");
  const time = (sample.time || "").replace(":", "");
  return `${project || "[Project]"}-Sample${index + 1}-${date || "[Date]"}-${time || "[Time]"}`;
}

function displayDate(iso) {
  const parts = String(iso || "").split("-");
  if (parts.length !== 3 || parts[0].length !== 4) return "";
  return `${parts[1]}/${parts[2]}/${parts[0]}`;
}

function parseUsDate(text) {
  const match = String(text || "").trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return "";
  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return "";
  const iso = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  const check = new Date(`${iso}T12:00:00`);
  if (check.getFullYear() !== year || check.getMonth() + 1 !== month || check.getDate() !== day) return "";
  return iso;
}

function ReportDateInput({ value, onChange, className }) {
  const [text, setText] = useState("");
  const [focused, setFocused] = useState(false);

  return (
    <div className="relative">
      <input
        type="text"
        inputMode="numeric"
        placeholder="MM/DD/YYYY"
        className={`${className} pr-8`}
        value={focused ? text : displayDate(value)}
        onFocus={() => {
          setFocused(true);
          setText(displayDate(value));
        }}
        onChange={(event) => {
          const next = event.target.value;
          setText(next);
          if (!next.trim()) onChange("");
          const iso = parseUsDate(next);
          if (iso) onChange(iso);
        }}
        onBlur={() => {
          setFocused(false);
          const iso = parseUsDate(text);
          if (iso) onChange(iso);
        }}
      />
      <input
        type="date"
        aria-label="Choose date"
        className="absolute inset-y-0 right-0 w-8 cursor-pointer opacity-0"
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

/** Looks up the day's weather for a project address. Resolves `{ line, place }` or `{ error }`. */
async function fetchWeatherLine(address, date, getAuthConfig) {
  const base = process.env.REACT_APP_BACKEND_BASEURL;
  const config = await getAuthConfig();
  try {
    const response = await axios.get(`${base}/api/weather`, {
      ...config,
      params: { address, date },
    });
    const line = response.data?.data?.line;
    if (!line) return { error: "No weather data for that location and date" };
    return { line, place: response.data?.data?.place || "" };
  } catch (error) {
    const message = error?.response?.data?.message;
    if (message) return { error: message };
    throw error;
  }
}

function shrinkImage(file) {
  return new Promise((resolve) => {
    if (!file) {
      resolve("");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const image = new Image();
      image.onload = () => {
        const scale = Math.min(1, 900 / Math.max(image.width, image.height));
        const canvas = document.createElement("canvas");
        canvas.width = image.width * scale;
        canvas.height = image.height * scale;
        canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.7));
      };
      image.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function Field({ label, hint, children, compact }) {
  return (
    <label className="block">
      <span className={`block min-h-4 text-[11px] font-semibold uppercase leading-4 tracking-wide text-stone-500 ${compact ? "sample-field-label" : ""}`}>
        {label}
        {hint ? <span className="ml-1 font-normal normal-case tracking-normal text-stone-400">{hint}</span> : null}
      </span>
      {children}
    </label>
  );
}

function Panel({ title, extra, children }) {
  return (
    <section className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
      {title ? (
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-stone-100 px-4 py-3">
          <h2 className="text-sm font-bold text-stone-900">{title}</h2>
          {extra ? <div className="max-w-full">{extra}</div> : null}
        </div>
      ) : null}
      <div className="p-4">{children}</div>
    </section>
  );
}

function CompleteStat({ label, value }) {
  return (
    <Field label={label}>
      <input
        disabled
        className={disabledInputClass}
        value={value == null ? "–" : `${Number.isInteger(value) ? value : value.toFixed(1)}%`}
      />
    </Field>
  );
}

const inputClass = "mt-1.5 w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm text-stone-800 outline-none transition focus:outline-none focus:ring-1 focus:ring-blue-500";
const disabledInputClass = "mt-1.5 w-full cursor-not-allowed rounded-lg border border-stone-200 bg-stone-100 px-3 py-2 text-sm font-semibold text-stone-500 outline-none";

function QtyField({ label, unit, value }) {
  return (
    <Field label={label} hint={unit ? `(${unit})` : ""}>
      <input
        disabled
        className={disabledInputClass}
        value={value == null ? "" : formatQty(value)}
      />
    </Field>
  );
}

export default function ElastizellDailyReport({ projectData, preparedBy }) {
  const { getAuthConfig, isReady } = useAuthFetch();
  const { instance, accounts } = useMsal();
  const account = accounts?.[0];
  const projectId = projectStorageId(projectData);
  const projectCode = sharePointProjectKey(projectData);
  // The working copy is per project id. Project details fill in through their own effects.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initial = useMemo(() => loadWorkingCopy(projectData, preparedBy), [projectId]);
  const [values, setValues] = useState(initial.v);
  const [samples, setSamples] = useState(initial.samples);
  const [photos, setPhotos] = useState(initial.photos);
  const [reportId, setReportId] = useState(initial.reportId);
  const [reportName, setReportName] = useState(initial.name);
  const [userEdited, setUserEdited] = useState(initial.userEdited);
  const [savedReports, setSavedReports] = useState([]);
  const [listVersion, setListVersion] = useState(0);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [reportQuery, setReportQuery] = useState("");
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusNote, setStatusNote] = useState("");
  const [wxStatus, setWxStatus] = useState("");
  const [productionLines, setProductionLines] = useState([]);
  const [prodStatus, setProdStatus] = useState("");
  const sigRef = useRef(null);
  const sheetRef = useRef(null);
  const chooserRef = useRef(null);
  const reportSearchRef = useRef(null);
  const drawing = useRef(false);
  const wxArmed = useRef(false);
  const muteUntil = useRef(Date.now() + 300);
  const removedSharePointIds = useRef([]);
  const reportIdRef = useRef(initial.reportId);
  const nameTouched = useRef(Boolean(initial.reportId) || initial.userEdited);
  const pdfItemIdRef = useRef("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [discardAction, setDiscardAction] = useState(null);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [deletingReport, setDeletingReport] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const muteEdits = () => {
    muteUntil.current = Date.now() + 300;
  };

  const applyWorkingCopy = useCallback((loaded, edited) => {
    muteEdits();
    reportIdRef.current = loaded.reportId || null;
    setReportId(loaded.reportId || null);
    setReportName(loaded.name);
    setValues(loaded.v);
    setSamples(loaded.samples);
    setPhotos(loaded.photos);
    setUserEdited(Boolean(edited));
    setPreview(false);
  }, []);

  useEffect(() => {
    const loaded = loadWorkingCopy(projectData, preparedBy);
    nameTouched.current = Boolean(loaded.reportId) || loaded.userEdited;
    pdfItemIdRef.current = "";
    setPdfUrl("");
    setSavedReports([]);
    removedSharePointIds.current = [];
    applyWorkingCopy(loaded, loaded.userEdited);
    // Reload only when the selected project changes. Later project-detail fills update fields in place.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Re-apply project details whenever the project data they come from changes,
  // including the same localStorage event Project Info uses.
  useEffect(() => {
    const apply = (data) => {
      muteEdits();
      setValues((current) => applyProjectDetails({ ...current }, data || projectData, preparedBy));
    };
    apply(projectData);
    const onProjectDetails = (event) => apply(event.detail || storedProjectDetails());
    window.addEventListener("projectDetailsChanged", onProjectDetails);
    return () => window.removeEventListener("projectDetailsChanged", onProjectDetails);
  }, [
    projectData, preparedBy,
    projectData?.projectName, projectData?.projectId, projectData?.projectNo, projectData?.customerName,
    projectData?.street, projectData?.city, projectData?.state, projectData?.zipCode,
    projectData?.projectAddress, projectData?.scopeDescription, projectData?.scope, projectData?.id_project,
  ]);

  // Load the project straight from the API so the form never depends on a half-filled saved copy.
  useEffect(() => {
    const opportunityId = projectData?.id_project;
    if (!opportunityId || !isReady) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_BASEURL;
        const config = await getAuthConfig();
        const opportunity = await axios.get(`${base}/api/salesforce/opportunity/${encodeURIComponent(opportunityId)}`, config);
        const record = opportunity.data?.records?.[0];
        if (cancelled || !record) return;

        let infoPayload = {};
        if (record.Project__c) {
          try {
            const info = await axios.get(`${base}/api/salesforce/projects/${encodeURIComponent(record.Project__c)}/Info`, config);
            infoPayload = info.data || {};
          } catch {
            /* address falls back to the opportunity record */
          }
        }
        if (cancelled) return;

        const project = record.Project__r || {};
        const infoRecords = infoPayload.records && typeof infoPayload.records === "object" ? infoPayload.records : {};
        const address = projectAddressFrom({
          ...record,
          ...project,
          Address: infoRecords.Address || infoPayload.Address || record.Address,
        }) || projectAddressFrom(projectData);
        const found = {
          pname: meaningfulText(project.Name) || meaningfulText(record.Name),
          pno: meaningfulText(project.Project_System_Wide_ID__c),
          gc: meaningfulText(record.Contract_With__r?.Name),
          paddr: address,
        };
        const scopeText = meaningfulText(record.Project_Scope__c) || meaningfulText(record.dv_Type__c);

        muteEdits();
        setValues((current) => {
          const next = { ...current };
          Object.keys(found).forEach((key) => {
            if (found[key]) next[key] = found[key];
          });
          if (scopeText && isPlaceholder(next.scope, DEFAULT_SCOPE)) next.scope = scopeText;
          return next;
        });
      } catch {
        /* keep whatever the project data already provided */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectData?.id_project, isReady, getAuthConfig]);

  useEffect(() => {
    const projectCode = projectData?.projectNo || projectData?.projectId;
    const reportDate = values.date;
    if (!projectCode || !reportDate || !isReady) return undefined;

    let cancelled = false;
    const loadProduction = async () => {
      setProdStatus("loading");
      try {
        const base = process.env.REACT_APP_BACKEND_BASEURL;
        const config = await getAuthConfig();
        const listUrl = `${base}/api/miter/production-performance/${encodeURIComponent(projectCode)}`;
        const listResponse = await axios.get(listUrl, config);
        if (cancelled) return;
        if (listResponse.data?.status !== "success") {
          setProductionLines([]);
          setProdStatus("empty");
          return;
        }
        const scopes = (Array.isArray(listResponse.data?.data?.rows) ? listResponse.data.data.rows : [])
          .filter((row) => row?.scopeId);
        if (!scopes.length) {
          setProductionLines([]);
          setProdStatus("empty");
          return;
        }

        const payloads = await Promise.all(scopes.map(async (scopeRow) => {
          const scopeName = scopeRow.scope || "";
          const path = `${base}/api/miter/production-performance/${encodeURIComponent(projectCode)}/log-production-data/${encodeURIComponent(scopeRow.scopeId)}`;
          const url = scopeName ? `${path}?scope=${encodeURIComponent(scopeName)}` : path;
          try {
            const response = await axios.get(url, config);
            if (response.data?.status !== "success") return null;
            return {
              scopeId: String(scopeRow.scopeId),
              scope: scopeName,
              data: response.data.data || {},
            };
          } catch {
            return null;
          }
        }));
        if (cancelled) return;

        const lines = [];
        payloads.filter(Boolean).forEach((payload) => {
          const prodRows = Array.isArray(payload.data.prodRows) ? payload.data.prodRows : [];
          const dates = Array.isArray(payload.data.dates) ? payload.data.dates : [];
          prodRows.forEach((row, index) => {
            const name = String(row.description || row.activity || "").trim();
            if (!name) return;
            lines.push({
              key: `${payload.scopeId}|${productionItemKey(row)}|${index}`,
              scope: payload.scope,
              name,
              uom: String(row.uom || "").trim(),
              ...summarizeProductionLine(row, dates, reportDate),
            });
          });
        });

        setProductionLines(lines);
        setProdStatus(lines.length ? "ready" : "empty");
      } catch {
        if (!cancelled) {
          setProductionLines([]);
          setProdStatus("error");
        }
      }
    };

    loadProduction();
    return () => {
      cancelled = true;
    };
  }, [projectData?.projectNo, projectData?.projectId, values.date, isReady, getAuthConfig]);

  useEffect(() => {
    const name = currentUserName(preparedBy);
    if (!name) return;
    muteEdits();
    setValues((current) => (current.by ? current : { ...current, by: name }));
  }, [preparedBy, projectId]);

  useEffect(() => {
    if (Date.now() < muteUntil.current) return;
    setUserEdited(true);
  }, [values, samples, photos, reportName]);

  useEffect(() => {
    if (!projectId || !userEdited) return undefined;
    const timer = setTimeout(() => {
      try {
        const savedValues = { ...values };
        if (isPlaceholder(savedValues.paddr)) savedValues.paddr = "";
        writeDraft(projectId, {
          reportId: reportIdRef.current,
          name: reportName,
          userEdited,
          v: savedValues,
          s: samples,
          p: photos,
        });
      } catch {
        setStatusNote("This device couldn't keep a local copy. Save to store the report.");
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [values, photos, samples, projectId, reportName, userEdited]);

  useEffect(() => {
    if (!projectCode || !isReady) return undefined;
    let cancelled = false;
    listDailyReports(getAuthConfig, projectCode)
      .then((rows) => {
        if (!cancelled) setSavedReports(rows);
      })
      .catch((error) => {
        if (!cancelled) setStatusNote(error.message);
      });
    return () => {
      cancelled = true;
    };
  }, [projectCode, isReady, getAuthConfig, listVersion]);

  useEffect(() => {
    if (reportIdRef.current || nameTouched.current) return;
    const next = defaultReportName(values.date, savedReports.map((report) => report.name));
    if (reportName === next) return;
    muteEdits();
    setReportName(next);
  }, [savedReports, values.date, reportName]);

  useEffect(() => {
    const pending = photos.filter((photo) => photo.sharepointItemId && photo.img == null && !photo.imageFailed);
    if (!pending.length || !account) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const token = await getGraphToken(instance, account);
        const updates = new Map();
        for (const photo of pending) {
          try {
            const blob = await downloadProjectFile(token, "Photos", photo.sharepointItemId);
            updates.set(photo.sharepointItemId, { img: URL.createObjectURL(blob), imageFailed: false });
          } catch {
            updates.set(photo.sharepointItemId, { img: null, imageFailed: true });
          }
        }
        if (cancelled) return;
        muteEdits();
        setPhotos((current) => current.map((photo) => {
          const update = updates.get(photo.sharepointItemId);
          if (!update || photo.img) return photo;
          return { ...photo, ...update };
        }));
      } catch {
        /* the photo stays as a file the user can open after the next save */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [photos, instance, account]);

  useEffect(() => {
    if (!chooserOpen) return undefined;
    const onPointerDown = (event) => {
      if (!chooserRef.current?.contains(event.target)) setChooserOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setChooserOpen(false);
    };
    window.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    reportSearchRef.current?.focus();
    return () => {
      window.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [chooserOpen]);

  const setValue = (key, value) => setValues((current) => ({ ...current, [key]: value }));

  const volume = parseFloat(values.vol);
  const densities = samples.map((sample) => sampleDensity(sample, volume));
  const filledDensities = densities.filter((value) => value != null);
  const average = filledDensities.length
    ? (filledDensities.reduce((sum, value) => sum + value, 0) / filledDensities.length).toFixed(1)
    : "–";
  const scopeCount = new Set(productionLines.map((line) => line.scope).filter(Boolean)).size;

  const paintSignature = useCallback(() => {
    const canvas = sigRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width) return;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = Math.round(rect.width * ratio);
    canvas.height = Math.round(rect.height * ratio);
    const context = canvas.getContext("2d");
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.lineWidth = 2.2;
    context.lineCap = "round";
    context.lineJoin = "round";
    context.strokeStyle = "#111";
    if (values.sig) {
      const image = new Image();
      image.onload = () => context.drawImage(image, 0, 0, rect.width, rect.height);
      image.src = values.sig;
    }
  }, [values.sig]);

  useEffect(() => {
    paintSignature();
    const canvas = sigRef.current;
    if (!canvas || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => paintSignature());
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [paintSignature]);

  const pointerPos = (event) => {
    const rect = sigRef.current.getBoundingClientRect();
    return [event.clientX - rect.left, event.clientY - rect.top];
  };

  const onPointerDown = (event) => {
    const canvas = sigRef.current;
    drawing.current = true;
    canvas.setPointerCapture(event.pointerId);
    const context = canvas.getContext("2d");
    const [x, y] = pointerPos(event);
    context.beginPath();
    context.moveTo(x, y);
    context.lineTo(x + 0.01, y);
    context.stroke();
  };

  const onPointerMove = (event) => {
    if (!drawing.current) return;
    const context = sigRef.current.getContext("2d");
    const [x, y] = pointerPos(event);
    context.lineTo(x, y);
    context.stroke();
  };

  const onPointerUp = () => {
    if (!drawing.current) return;
    drawing.current = false;
    try {
      setValue("sig", sigRef.current.toDataURL("image/png"));
    } catch {
      /* canvas may be empty */
    }
  };

  const fillWeather = async () => {
    const address = (values.paddr || "").trim();
    const date = values.date;
    if (!address) {
      setWxStatus("Enter a project address first");
      return;
    }
    if (!date) {
      setWxStatus("Enter the report date first");
      return;
    }
    if (values.wx?.trim() && !wxArmed.current) {
      wxArmed.current = true;
      setWxStatus("Weather already has text. Tap again to overwrite it.");
      setTimeout(() => {
        wxArmed.current = false;
      }, 4000);
      return;
    }
    wxArmed.current = false;
    setWxStatus("Looking up weather for the project location…");
    try {
      const result = await fetchWeatherLine(address, date, getAuthConfig);
      if (result.error) {
        setWxStatus(result.error);
        return;
      }
      setValue("wx", result.line);
      setWxStatus(result.place ? `Filled for ${result.place}` : `Filled for ${displayDate(date) || date}`);
    } catch (error) {
      setWxStatus(`Lookup failed (${error?.message || error}). Check your connection or type conditions in the box.`);
    }
  };

  // Fill weather automatically once the address and date are known and the box is empty.
  const weatherTried = useRef("");
  useEffect(() => {
    const address = (values.paddr || "").trim();
    const date = values.date;
    if (!isReady || !address || !date || values.wx?.trim()) return undefined;
    const key = `${projectId}|${address}|${date}`;
    if (weatherTried.current === key) return undefined;
    weatherTried.current = key;
    let cancelled = false;
    fetchWeatherLine(address, date, getAuthConfig)
      .then((result) => {
        if (cancelled) return;
        if (result.error) {
          setWxStatus(result.error);
          return;
        }
        setValues((current) => (current.wx?.trim() ? current : { ...current, wx: result.line }));
        setWxStatus(result.place ? `Filled for ${result.place}` : `Filled for ${displayDate(date) || date} from the project address`);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (weatherTried.current === key) weatherTried.current = "";
    };
  }, [values.paddr, values.date, values.wx, projectId, isReady, getAuthConfig]);

  const runOrConfirmDiscard = (action) => {
    if (!userEdited) {
      action();
      return;
    }
    setDiscardAction(() => action);
  };

  const beginNewReport = (force = false) => {
    const startFreshReport = () => {
      const keep = {
        pname: values.pname,
        paddr: values.paddr,
        pno: values.pno,
        by: values.by,
        gc: values.gc,
        scope: values.scope,
        epstd: values.epstd,
        toptd: values.toptd,
        slurrytot: values.slurrytot,
        toptot: values.toptot,
        vol: values.vol,
      };
      const next = freshReport(projectData, preparedBy);
      const date = todayIso();
      const savedNames = savedReports.map((report) => report.name);
      nameTouched.current = false;
      pdfItemIdRef.current = "";
      setPdfUrl("");
      removedSharePointIds.current = [];
      clearDraft(projectId);
      applyWorkingCopy({
        v: { ...next.v, ...keep, date, sdate: date },
        samples: next.samples,
        photos: [],
        reportId: null,
        name: defaultReportName(date, savedNames),
      }, false);
      setStatusNote("");
      setChooserOpen(false);
    };
    if (force) {
      startFreshReport();
      return;
    }
    runOrConfirmDiscard(startFreshReport);
  };

  const openSavedReport = (id) => {
    if (id === reportIdRef.current) {
      setChooserOpen(false);
      return;
    }
    runOrConfirmDiscard(() => {
      setStatusNote("Opening report…");
      getDailyReport(getAuthConfig, id)
        .then((report) => {
          const loaded = reportFromServer(report, projectData, preparedBy);
          nameTouched.current = true;
          pdfItemIdRef.current = report.pdfItemId || "";
          setPdfUrl(report.pdfWebUrl || "");
          removedSharePointIds.current = [];
          clearDraft(projectId);
          applyWorkingCopy(loaded, false);
          setStatusNote(`Saved ${formatSavedAt(report.updatedAt)}`);
          setChooserOpen(false);
        })
        .catch((error) => {
          setStatusNote(error.message);
        });
    });
  };

  const saveReport = async () => {
    const name = reportName.trim();
    if (!name) {
      setStatusNote("Enter a report file name before saving.");
      return;
    }
    if (!projectCode) {
      setStatusNote("Select a project before saving.");
      return;
    }
    if (!values.date) {
      setStatusNote("Enter the report date before saving.");
      return;
    }
    setSaving(true);
    setStatusNote("Saving…");
    const { sig, ...form } = values;
    const payload = {
      projectCode,
      opportunityId: projectData?.id_project || null,
      name,
      reportDate: values.date,
      form,
      samples,
      signature: sig || null,
    };
    try {
      let id = reportIdRef.current;
      if (!id) {
        const created = await createDailyReport(getAuthConfig, payload);
        id = created.id;
        reportIdRef.current = id;
        muteEdits();
        setReportId(id);
      } else {
        await updateDailyReport(getAuthConfig, id, payload);
      }

      const token = await getGraphToken(instance, account);
      let pdfItemId = pdfItemIdRef.current || "";
      let pdfWebUrl = "";
      let pdfName = "";
      try {
        if (sheetRef.current) {
          const sheet = sheetRef.current;
          const images = [...sheet.querySelectorAll("img")];
          await Promise.all(images.map((image) => (image.complete ? null : new Promise((resolve) => {
            image.onload = resolve;
            image.onerror = resolve;
          }))));
          const pdfBlob = await elementToPdfBlob(sheet);
          pdfName = pdfFileName(name, id);
          const previousPdfId = pdfItemId;
          const uploadedPdf = await uploadProjectFile(token, {
            library: "Reports",
            segments: [projectCode, "Daily Reports"],
            fileName: pdfName,
            blob: pdfBlob,
          });
          pdfItemId = uploadedPdf.itemId;
          pdfWebUrl = uploadedPdf.webUrl;
          if (previousPdfId && previousPdfId !== pdfItemId) {
            try {
              await deleteProjectItem(token, "Reports", previousPdfId);
            } catch {
              /* the new PDF is already in SharePoint */
            }
          }
        }
      } catch (error) {
        muteEdits();
        setUserEdited(true);
        setStatusNote(`Report saved, but the PDF could not be uploaded. Save again to retry. ${error.message || ""}`.trim());
        setListVersion((current) => current + 1);
        setSaving(false);
        return;
      }

      const saved = await updateDailyReport(getAuthConfig, id, {
        photos: [],
        pdf: pdfItemId ? { fileName: pdfName, itemId: pdfItemId, webUrl: pdfWebUrl } : undefined,
      });
      pdfItemIdRef.current = saved.pdfItemId || pdfItemId || "";
      setPdfUrl(saved.pdfWebUrl || pdfWebUrl || "");
      try {
        await deleteProjectPath(token, "Photos", [projectCode, "Daily Reports", id]);
      } catch {
        /* photos are already inside the PDF */
      }
      removedSharePointIds.current = [];
      clearDraft(projectId);
      muteEdits();
      setUserEdited(false);
      setStatusNote(`Saved ${formatSavedAt(saved.updatedAt)}`);
      setListVersion((current) => current + 1);
    } catch (error) {
      if (reportIdRef.current) setListVersion((current) => current + 1);
      setStatusNote(error.message || "Couldn't save the report");
    } finally {
      setSaving(false);
    }
  };

  const downloadSavedPdf = async () => {
    const saved = savedReports.find((report) => report.id === reportId);
    const itemId = saved?.pdfItemId || pdfItemIdRef.current;
    if (!itemId || downloadingPdf) return;
    const fileName = saved?.pdfFileName || pdfFileName(saved?.name || reportName, reportId);
    setDownloadingPdf(true);
    try {
      const token = await getGraphToken(instance, account);
      const blob = await downloadProjectFile(token, "Reports", itemId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setStatusNote(error.message || "Couldn't download the PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const deleteSavedReport = async (report) => {
    const label = report?.name || "this report";
    setDeletingReport(true);
    setStatusNote("Deleting…");
    try {
      await deleteDailyReport(getAuthConfig, report.id);
      let fileWarning = "";
      try {
        const token = await getGraphToken(instance, account);
        await deleteProjectPath(token, "Photos", [projectCode, "Daily Reports", report.id]);
        if (report.pdfItemId) await deleteProjectItem(token, "Reports", report.pdfItemId);
      } catch {
        fileWarning = " Some SharePoint files could not be removed.";
      }
      if (report.id === reportIdRef.current) beginNewReport(true);
      setListVersion((current) => current + 1);
      setStatusNote(`Deleted ${label}.${fileWarning}`);
      setChooserOpen(false);
    } catch (error) {
      setStatusNote(error.message);
    } finally {
      setDeletingReport(false);
      setReportToDelete(null);
    }
  };

  const addPhoto = async (file, index) => {
    const image = await shrinkImage(file);
    if (!image) return;
    setPhotos((current) => current.map((photo, photoIndex) => {
      if (photoIndex !== index) return photo;
      if (photo.sharepointItemId) removedSharePointIds.current.push(photo.sharepointItemId);
      return { ...photo, img: image, sharepointItemId: "", webUrl: "", fileName: "", imageFailed: false };
    }));
  };

  const removePhoto = (index) => {
    setPhotos((current) => {
      const photo = current[index];
      if (photo?.sharepointItemId) removedSharePointIds.current.push(photo.sharepointItemId);
      return current.filter((_, rowIndex) => rowIndex !== index);
    });
  };

  const printReport = () => window.print();

  const reportSearch = reportQuery.trim().toLowerCase();
  const visibleReports = savedReports.filter((report) => (
    !reportSearch || String(report.name || "").toLowerCase().includes(reportSearch)
  ));
  const selectedReportLabel = reportId
    ? (savedReports.find((report) => report.id === reportId)?.name || reportName)
    : "";

  const openReportChooser = () => {
    setReportQuery("");
    setChooserOpen((open) => !open);
  };

  const saveLabel = saving ? "Saving…" : reportId ? "Update" : "Save";
  let statusLine = reportId ? "Saved" : "Not saved";
  if (userEdited) statusLine = "Unsaved changes";
  if (statusNote && (!userEdited || !/^Saved\b|^Deleted\b|^Opening\b/.test(statusNote))) statusLine = statusNote;
  if (saving) statusLine = "Saving…";

  return (
    <div className="daily-report h-full overflow-y-auto bg-stone-50 text-stone-800">
      {saving ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/80 text-sm font-bold text-stone-700">
          Saving report and PDF…
        </div>
      ) : null}
      <div className="daily-report-screen flex w-full flex-col gap-4 px-4 py-4 pb-10 lg:px-6 min-[1025px]:pt-9">
        <div className="relative z-30 flex flex-col gap-3 overflow-visible rounded-xl border border-stone-200 bg-white px-4 py-3 shadow-sm">
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-wide text-red-600">Daily report</p>
            <h1 className="text-base font-bold text-stone-900">Elastizell 250 PSI LWIC</h1>
            <p className="text-[11px] text-stone-400">{statusLine}</p>
          </div>
          <div className="daily-report-toolbar">
            <label className="daily-report-name text-[11px] font-semibold uppercase tracking-wide text-stone-500">
              Report File Name
              <input
                className={`${inputClass} w-full`}
                value={reportName}
                maxLength={160}
                placeholder="Report File Name"
                onChange={(event) => {
                  nameTouched.current = true;
                  setReportName(event.target.value);
                }}
              />
            </label>
            <label className="daily-report-date text-[11px] font-semibold uppercase tracking-wide text-stone-500">
              Report date
              <ReportDateInput className={`${inputClass} w-full`} value={values.date} onChange={(next) => setValue("date", next)} />
            </label>
            <div ref={chooserRef} className="daily-report-saved">
              <span className="block text-[11px] font-semibold uppercase tracking-wide text-stone-500">Saved Reports</span>
              <div className="relative">
              <button
                type="button"
                aria-haspopup="listbox"
                aria-expanded={chooserOpen}
                onClick={openReportChooser}
                className={`${inputClass} flex items-center justify-between gap-2 text-left font-normal ${chooserOpen ? "border-blue-500 ring-1 ring-blue-500" : ""}`}
              >
                <span className={`min-w-0 flex-1 truncate ${selectedReportLabel ? "text-stone-800" : "text-stone-400"}`}>
                  {selectedReportLabel || "Select a report"}
                </span>
                <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className={`h-4 w-4 shrink-0 text-stone-400 transition ${chooserOpen ? "rotate-180" : ""}`}>
                  <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.17l3.71-3.94a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06z" clipRule="evenodd" />
                </svg>
              </button>
              {chooserOpen ? (
                <div className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-lg border border-stone-200 bg-white shadow-lg">
                  <div className="border-b border-stone-100 p-2">
                    <input
                      ref={reportSearchRef}
                      value={reportQuery}
                      onChange={(event) => setReportQuery(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && visibleReports.length === 1) {
                          event.preventDefault();
                          openSavedReport(visibleReports[0].id);
                        }
                      }}
                      placeholder="Search by report file name"
                      className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm text-stone-800 outline-none focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div className="max-h-64 overflow-y-auto py-1" role="listbox">
                    {savedReports.length === 0 ? <p className="px-3 py-3 text-sm text-stone-500">No saved reports yet.</p> : null}
                    {savedReports.length > 0 && visibleReports.length === 0 ? (
                      <p className="px-3 py-3 text-sm text-stone-500">No reports match “{reportQuery.trim()}”.</p>
                    ) : null}
                    {visibleReports.map((report) => (
                      <div key={report.id} className={`flex items-center gap-2 px-2 py-1.5 ${report.id === reportId ? "bg-red-50" : "hover:bg-stone-50"}`}>
                        <button type="button" onClick={() => openSavedReport(report.id)} className="min-w-0 flex-1 rounded-md px-1 py-1 text-left">
                          <div className="truncate text-sm font-semibold text-stone-800">{report.name}</div>
                          <div className="truncate text-[11px] text-stone-400">
                            {displayDate(report.reportDate) || "No date"}
                            {report.updatedAt ? ` · Saved ${formatSavedAt(report.updatedAt)}` : ""}
                          </div>
                        </button>
                        {report.pdfWebUrl ? (
                          <a href={report.pdfWebUrl} target="_blank" rel="noreferrer" className="shrink-0 rounded px-1.5 py-1 text-xs font-bold text-red-600 hover:bg-red-50">
                            PDF
                          </a>
                        ) : null}
                        <button type="button" onClick={() => setReportToDelete(report)} className="shrink-0 rounded px-1.5 py-1 text-xs font-bold text-red-600 hover:bg-red-50">
                          Delete
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              </div>
            </div>
            <div className="daily-report-actions">
            <button type="button" onClick={() => beginNewReport(false)} className="shrink-0 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-bold text-blue-700 hover:border-blue-300 hover:bg-blue-100">
              New report
            </button>
            <button type="button" onClick={() => setPreview((current) => !current)} className="shrink-0 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-sm font-bold text-violet-700 hover:border-violet-300 hover:bg-violet-100">
              {preview ? "Edit" : "Preview"}
            </button>
            {pdfUrl ? (
              <a href={pdfUrl} target="_blank" rel="noreferrer" className="shrink-0 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm font-bold text-sky-700 hover:border-sky-300 hover:bg-sky-100">
                Open PDF
              </a>
            ) : (
              <button type="button" disabled className="shrink-0 cursor-not-allowed rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-sm font-bold text-stone-300">
                Open PDF
              </button>
            )}
            <button
              type="button"
              aria-label="Download PDF"
              title="Download PDF"
              disabled={!pdfUrl || downloadingPdf}
              onClick={downloadSavedPdf}
              className={`inline-flex shrink-0 items-center justify-center rounded-lg border p-2 ${pdfUrl ? "border-teal-200 bg-teal-50 text-teal-700 hover:border-teal-300 hover:bg-teal-100" : "cursor-not-allowed border-stone-200 bg-stone-50 text-stone-300"}`}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v11m0 0 4-4m-4 4-4-4M5 20h14" />
              </svg>
            </button>
            <button
              type="button"
              disabled={!reportId || deletingReport}
              onClick={() => {
                if (!reportId) return;
                const saved = savedReports.find((report) => report.id === reportId);
                setReportToDelete({
                  id: reportId,
                  name: saved?.name || reportName,
                  pdfItemId: saved?.pdfItemId || pdfItemIdRef.current || "",
                });
              }}
              className={`shrink-0 rounded-lg border px-3 py-2 text-sm font-bold ${reportId ? "border-red-200 bg-red-50 text-red-600 hover:border-red-300 hover:bg-red-100" : "cursor-not-allowed border-stone-200 bg-stone-50 text-stone-300"}`}
            >
              Delete
            </button>
            <button type="button" onClick={saveReport} disabled={saving} className="shrink-0 rounded-lg bg-red-600 px-4 py-2 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-60">
              {saveLabel}
            </button>
            </div>
          </div>
        </div>

        {!preview && <>
        <Panel title="Project">
          <div className="daily-report-fields-3">
            <Field label="Project name"><input className={inputClass} value={values.pname} onChange={(event) => setValue("pname", event.target.value)} /></Field>
            <Field label="Project no."><input className={inputClass} value={values.pno} onChange={(event) => setValue("pno", event.target.value)} /></Field>
            <Field label="Prepared by"><input className={inputClass} value={values.by} onChange={(event) => setValue("by", event.target.value)} /></Field>
            <div className="daily-report-span-2">
              <Field label="Project address"><input className={inputClass} value={values.paddr} onChange={(event) => setValue("paddr", event.target.value)} /></Field>
            </div>
            <Field label="Customer / GC"><input className={inputClass} value={values.gc} onChange={(event) => setValue("gc", event.target.value)} /></Field>
            <div className="daily-report-span-3">
              <Field label="Scope description">
                <textarea className={`${inputClass} min-h-[72px]`} value={values.scope} onChange={(event) => setValue("scope", event.target.value)} />
              </Field>
            </div>
          </div>
        </Panel>

        <Panel title="Production summary">
          {prodStatus === "loading" ? <p className="text-xs text-stone-400">Loading production tracking…</p> : null}
          {prodStatus === "error" ? <p className="text-xs text-stone-400">Production tracking could not be loaded.</p> : null}
          {prodStatus === "empty" ? <p className="text-sm text-stone-500">No production tracking lines for this project.</p> : null}
          {prodStatus === "ready" ? (
            <div className="space-y-6">
              {productionLines.map((line) => {
                const totalShown = displayTotal(line.total);
                return (
                  <div key={line.key}>
                    <p className="text-xs font-bold uppercase tracking-wide text-stone-400">{line.name}</p>
                    {scopeCount > 1 && line.scope ? <p className="mb-3 text-[11px] text-stone-400">{line.scope}</p> : <div className="mb-3" />}
                    <div className="daily-report-fields-4 items-start">
                      <QtyField label="Installed today" unit={line.uom} value={line.today} />
                      <QtyField label="Installed to date" unit={line.uom} value={line.toDate} />
                      <QtyField label="Total estimated" unit={line.uom} value={totalShown} />
                      <CompleteStat label="% installed" value={line.percent} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}
        </Panel>

        <Panel title="Mix design / QC" extra={<span className="text-xs text-stone-400">Wet density = net weight ÷ bucket volume</span>}>
          <div className="daily-report-fields-3 mb-4">
            <Field label="Bucket volume" hint="cu ft">
              <input type="number" step="any" className={inputClass} value={values.vol} onChange={(event) => setValue("vol", event.target.value)} />
            </Field>
            <div className="daily-report-span-2 flex items-end">
              <div className="flex w-full flex-wrap items-center justify-between gap-2 rounded-lg border border-red-100 bg-red-50 px-4 py-2.5">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-red-700">Average wet density</span>
                <span className="text-2xl font-bold tabular-nums text-red-700">{average} <span className="text-xs font-semibold">pcf</span></span>
              </div>
            </div>
          </div>
          <div className="daily-report-sample-head px-1 pb-2 text-[11px] font-semibold uppercase tracking-wide text-stone-400">
            <span>Sample</span>
            <span>Pounds</span>
            <span>Ounces</span>
            <span>Time</span>
            <span>pcf</span>
          </div>
          <div className="space-y-3">
            {samples.map((sample, index) => (
              <div key={index} className="daily-report-sample">
                <div className="min-w-0">
                  <div className="text-sm font-bold text-stone-800">Sample {index + 1}</div>
                  <div className="truncate text-[11px] text-stone-400">{sampleName(values, sample, index)}</div>
                </div>
                <Field label="Pounds" compact>
                  <select className={inputClass} value={sample.nlb} onChange={(event) => setSamples((current) => current.map((row, rowIndex) => (rowIndex === index ? { ...row, nlb: event.target.value } : row)))}>
                    <option value="">–</option>
                    {Array.from({ length: 31 }, (_, offset) => offset + 5).map((pound) => <option key={pound} value={String(pound)}>{pound}</option>)}
                  </select>
                </Field>
                <Field label="Ounces" compact>
                  <select className={inputClass} value={sample.noz} onChange={(event) => setSamples((current) => current.map((row, rowIndex) => (rowIndex === index ? { ...row, noz: event.target.value } : row)))}>
                    <option value="">–</option>
                    {Array.from({ length: 16 }, (_, offset) => offset + 1).map((ounce) => <option key={ounce} value={String(ounce)}>{ounce}</option>)}
                  </select>
                </Field>
                <Field label="Time" compact>
                  <div className="mt-1.5 flex min-w-0 gap-2">
                    <input type="time" className={`${inputClass} mt-0 min-w-0`} value={sample.time} onChange={(event) => setSamples((current) => current.map((row, rowIndex) => (rowIndex === index ? { ...row, time: event.target.value } : row)))} />
                    <button
                      type="button"
                      className="shrink-0 rounded-lg border border-stone-200 px-2.5 text-xs font-bold text-stone-600 hover:border-red-200 hover:text-red-600"
                      onClick={() => {
                        const now = new Date();
                        const time = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
                        setSamples((current) => current.map((row, rowIndex) => (rowIndex === index ? { ...row, time } : row)));
                      }}
                    >
                      Now
                    </button>
                  </div>
                </Field>
                <div className="sample-density flex items-center justify-between gap-3 pb-2">
                  <div className="text-lg font-bold tabular-nums text-stone-900">
                    {densities[index] == null ? "–" : densities[index].toFixed(1)}
                  </div>
                  <button
                    type="button"
                    className="text-xs font-bold text-red-600"
                    onClick={() => setSamples((current) => current.filter((_, rowIndex) => rowIndex !== index))}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
          <button
            type="button"
            className="mt-4 rounded-lg border border-stone-200 px-3 py-2 text-sm font-bold text-stone-700 hover:border-red-200 hover:text-red-600"
            onClick={() => setSamples((current) => [...current, blankSample(true)])}
          >
            Add sample
          </button>
          <div className="mt-4">
            <Field label="Mix adjustments / QC notes">
              <textarea className={`${inputClass} min-h-[88px]`} value={values.qc} onChange={(event) => setValue("qc", event.target.value)} />
            </Field>
          </div>
        </Panel>

        <Panel title="Daily notes and planning">
          <div className="daily-report-fields-3">
            <div className="daily-report-span-3">
              <Field label="Weather">
                <textarea className={`${inputClass} min-h-[80px]`} value={values.wx} onChange={(event) => setValue("wx", event.target.value)} />
              </Field>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <button type="button" onClick={fillWeather} className="rounded-lg border border-stone-200 px-3 py-2 text-xs font-bold text-stone-700 hover:border-red-200 hover:text-red-600">
                  Auto-fill from address
                </button>
                {wxStatus ? <p className="text-xs text-stone-500">{wxStatus}</p> : null}
              </div>
            </div>
            {[
              ["coord", "Coordination / planning"],
              ["eq", "Equipment / downtime"],
              ["mat", "Material / deliveries / stock"],
            ].map(([key, label]) => (
              <Field key={key} label={label}>
                <textarea className={`${inputClass} min-h-[110px]`} value={values[key]} onChange={(event) => setValue(key, event.target.value)} />
              </Field>
            ))}
            <div className="daily-report-span-3 daily-report-fields-2">
              <Field label="Today's summary">
                <textarea className={`${inputClass} min-h-[110px]`} value={values.sum} onChange={(event) => setValue("sum", event.target.value)} />
              </Field>
              <Field label="Tomorrow's plan">
                <textarea className={`${inputClass} min-h-[110px]`} value={values.plan} onChange={(event) => setValue("plan", event.target.value)} />
              </Field>
            </div>
          </div>
        </Panel>

        <Panel title="Photos" extra={<span className="text-xs text-stone-400">{photos.length} of 12</span>}>
          <p className="mb-3 text-sm text-stone-500">Add up to 12 photos with a location and a note.</p>
          <div className="daily-report-photos-grid">
            {photos.map((photo, index) => (
              <div key={index} className="rounded-lg border border-stone-100 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-bold">Photo {index + 1}</span>
                  <button type="button" className="text-xs font-bold text-red-600" onClick={() => removePhoto(index)}>
                    Remove
                  </button>
                </div>
                <label className="relative mb-3 flex h-36 cursor-pointer items-center justify-center overflow-hidden rounded-lg border border-dashed border-stone-300 bg-stone-50 text-sm text-stone-400">
                  {photo.img ? <img src={photo.img} alt={`Photo ${index + 1}`} className="h-full w-full object-contain" /> : photo.imageFailed ? "Couldn't load photo" : photo.sharepointItemId ? "Loading photo…" : "Choose a photo"}
                  <input type="file" accept="image/*" className="absolute inset-0 cursor-pointer opacity-0" onChange={(event) => addPhoto(event.target.files?.[0], index)} />
                </label>
                <Field label="Location / area">
                  <input className={inputClass} value={photo.loc || ""} onChange={(event) => setPhotos((current) => current.map((row, rowIndex) => (rowIndex === index ? { ...row, loc: event.target.value } : row)))} />
                </Field>
                <div className="mt-3">
                  <Field label="Notes">
                    <textarea className={`${inputClass} min-h-[64px]`} value={photo.note || ""} onChange={(event) => setPhotos((current) => current.map((row, rowIndex) => (rowIndex === index ? { ...row, note: event.target.value } : row)))} />
                  </Field>
                </div>
              </div>
            ))}
          </div>
          {photos.length < 12 && (
            <button type="button" className="mt-4 rounded-lg border border-stone-200 px-3 py-2 text-sm font-bold text-stone-700 hover:border-red-200 hover:text-red-600" onClick={() => setPhotos((current) => [...current, {}])}>
              Add photo
            </button>
          )}
        </Panel>

        <Panel
          title="Sign-off"
          extra={
            <button type="button" className="text-xs font-bold text-red-600" onClick={() => setValue("sig", "")}>
              Clear signature
            </button>
          }
        >
          <div className="daily-report-fields-3">
            <div className="daily-report-span-2 relative min-h-[150px]">
              <canvas
                ref={sigRef}
                className="absolute inset-0 h-full w-full cursor-crosshair rounded-lg border border-stone-200 bg-stone-50"
                style={{ touchAction: "none" }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
              />
              {!values.sig && <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-stone-300">Sign here</div>}
            </div>
            <div className="flex flex-col gap-3">
              <Field label="Sign-off date">
                <ReportDateInput className={inputClass} value={values.sdate} onChange={(next) => setValue("sdate", next)} />
              </Field>
              <button type="button" onClick={printReport} className="rounded-lg border border-stone-200 px-4 py-2 text-sm font-bold text-stone-700 hover:border-red-200 hover:text-red-600">
                Print
              </button>
              <button type="button" onClick={saveReport} disabled={saving} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-60">
                {saveLabel}
              </button>
            </div>
          </div>
        </Panel>
        </>}
      </div>

      <article ref={sheetRef} className={`daily-report-sheet${preview ? " is-preview" : " is-capture"}`} aria-hidden={preview ? undefined : true}>
        <header className="sheet-head">
          <div className="sheet-title">Elastizell 250 PSI LWIC Daily Production &amp; QC Report</div>
          {displayDate(values.date) ? <div className="sheet-meta">Date: {displayDate(values.date)}</div> : null}
          <div className="sheet-sub">{[values.pname, values.paddr].filter(Boolean).join("   |   ")}</div>
        </header>
        <h2>Project</h2>
        <div className="sheet-grid">
          <SheetField label="Project Name" value={values.pname} />
          <SheetField label="Project Address" value={values.paddr} />
          <SheetField label="Project No." value={values.pno} />
          <SheetField label="Prepared By" value={values.by} />
          <SheetField label="Customer / GC" value={values.gc} />
        </div>
        <SheetField label="Scope Description" value={values.scope} tall />
        <h2>Production Summary</h2>
        {productionLines.length === 0 ? <SheetField label="Production tracking" value="–" /> : productionLines.map((line) => {
          const unit = line.uom ? ` (${line.uom})` : "";
          const totalShown = displayTotal(line.total);
          return (
            <div key={line.key} className="sheet-grid">
              <SheetField label={`${line.name} installed today${unit}`} value={formatQty(line.today)} />
              <SheetField label={`${line.name} total estimated${unit}`} value={totalShown == null ? "" : formatQty(totalShown)} />
              <SheetField label={`${line.name} installed to date${unit}`} value={formatQty(line.toDate)} />
              <SheetField label={`${line.name} % installed`} value={line.percent == null ? "–" : `${line.percent}%`} />
            </div>
          );
        })}
        <h2>Mix Design / QC</h2>
        <SheetField label="Bucket volume (cu ft)" value={values.vol} />
        {samples.map((sample, index) => {
          const lb = parseFloat(sample.nlb);
          const oz = parseFloat(sample.noz);
          const hasWeight = !Number.isNaN(lb) || !Number.isNaN(oz);
          return (
            <div key={index} className="sheet-field">
              <div className="sheet-label">Sample {index + 1} · {sampleName(values, sample, index)}</div>
              <div className="sheet-value">
                {hasWeight ? `Net Weight: ${Number.isNaN(lb) ? 0 : lb} lb ${Number.isNaN(oz) ? 0 : oz} oz` : "Net Weight: –"}
                {` · Time: ${sample.time || "–"} · Wet Density: ${densities[index] == null ? "–" : `${densities[index].toFixed(1)} pcf`}`}
              </div>
            </div>
          );
        })}
        <SheetField label="Average Wet Density (pcf)" value={average} />
        <SheetField label="Mix Adjustments / QC Notes" value={values.qc} tall />
        <h2>Daily Notes &amp; Planning</h2>
        <SheetField label="Weather" value={values.wx} tall />
        <SheetField label="Coordination Concerns / Planning" value={values.coord} tall />
        <SheetField label="Equipment Issues / Downtime" value={values.eq} tall />
        <SheetField label="Material Notes / Deliveries / Stock" value={values.mat} tall />
        <SheetField label="Today's Summary" value={values.sum} tall />
        <SheetField label="Tomorrow's Plan" value={values.plan} tall />
        {photos.some((photo) => photo.img || photo.loc || photo.note) ? (
          <>
            <h2>Photos &amp; Notes</h2>
            <div className="sheet-photos">
              {photos.map((photo, index) => (
                photo.img || photo.loc || photo.note ? (
                  <div key={index}>
                    <div className="sheet-label">Photo {index + 1}</div>
                    {photo.img ? <img src={photo.img} alt="" /> : null}
                    <SheetField label="Location / Area" value={photo.loc} />
                    <SheetField label="Notes" value={photo.note} />
                  </div>
                ) : null
              ))}
            </div>
          </>
        ) : null}
        <h2>Sign-off</h2>
        <div className="sheet-label">Supervisor Signature</div>
        <div className="sheet-sign">{values.sig ? <img src={values.sig} alt="Supervisor signature" /> : null}</div>
        <SheetField label="Date" value={displayDate(values.sdate)} />
      </article>
      <ConfirmDialog
        isOpen={Boolean(discardAction)}
        title="Discard changes"
        message="This report has unsaved changes. Discard them?"
        confirmLabel="Discard"
        cancelLabel="Cancel"
        isDestructive
        onConfirm={() => {
          const action = discardAction;
          setDiscardAction(null);
          action?.();
        }}
        onClose={() => setDiscardAction(null)}
      />
      <ConfirmDialog
        isOpen={Boolean(reportToDelete)}
        title="Delete report"
        message={reportToDelete ? `Delete "${reportToDelete.name || "this report"}"? Its PDF will also be removed from SharePoint.` : ""}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isDestructive
        isLoading={deletingReport}
        onConfirm={() => {
          if (reportToDelete) deleteSavedReport(reportToDelete);
        }}
        onClose={() => {
          if (!deletingReport) setReportToDelete(null);
        }}
      />
    </div>
  );
}

function SheetField({ label, value, tall }) {
  return (
    <div className="sheet-field">
      <div className="sheet-label">{label}</div>
      <div className={`sheet-value${tall ? " tall" : ""}`}>{value || ""}</div>
    </div>
  );
}

